
import { useState, useEffect, useCallback } from "react";
import { BirdieRecord, getManualPlayerId } from "@/types";
import { toast } from "@/hooks/use-toast";
import { getScores } from "@/integrations/supabase/scores";

/**
 * Fetches birdie records for a user on a specific course.
 * Optionally filters to a single hole number.
 * In game mode, skip this entirely (game history uses useGameBirdieRecords).
 */
export function useBirdieRecords(
  userId: string,
  courseId: string,
  holeNumber?: number,
  skip = false
) {
  const [birdieRecords, setBirdieRecords] = useState<BirdieRecord[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const fetchBirdieRecords = useCallback(async () => {
    if (skip) return;
    setIsLoading(true);
    try {
      const mpId = getManualPlayerId(userId);
      const scores = await getScores({
        ...(mpId ? { manualPlayerId: mpId } : { userId }),
        courseId,
        holeNumber,
      });

      if (scores.length === 0) {
        setBirdieRecords([]);
        setIsLoading(false);
        return;
      }

      // Map scores to BirdieRecord — no per-score getScoreGames query needed
      const records: BirdieRecord[] = scores.map((score) => ({
        id: score.id,
        userId: mpId ? userId : score.user_id,
        courseId: score.course_id,
        holeNumber: score.hole_number,
        teeBox: score.tee,
        scoreType: score.score_type,
        date: score.score_date.split('T')[0],
        notes: score.notes || "",
        gameIds: [], // Not used by history display; game linking is server-side
      }));

      // Sort by date (newest first)
      setBirdieRecords(records.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()));
    } catch (error) {
      console.error("Error fetching birdie records:", error);
      toast({
        title: "Error",
        description: "Failed to load birdie records.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [userId, courseId, holeNumber, skip]);

  useEffect(() => {
    fetchBirdieRecords();
  }, [fetchBirdieRecords, fetchTrigger]);

  const refreshRecords = useCallback(() => {
    setFetchTrigger(prev => prev + 1);
  }, []);

  return {
    birdieRecords,
    isLoading,
    fetchBirdieRecords,
    refreshRecords,
  };
}
